import type { DataGridExportFileName } from '../types';

/**
 * Slugify a string into a file-name-friendly form.
 * Strips accents, lowercases, collapses runs of non-alphanumeric chars
 * to a single `_`, and trims leading / trailing separators.
 */
function slugify(value: string): string {
  return value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
}

/**
 * Build the final file name slug (without extension) from either a plain
 * string (returned as-is after trimming) or a structured descriptor.
 *
 * Structured form:
 *   { base: 'rapport_stock',
 *     brand: 'Quick Maroc Prod',
 *     period: { from: '2026-04-01', to: '2026-04-14' } }
 *   → 'rapport_stock_quick_maroc_prod_2026-04-01_au_2026-04-14'
 *
 * When no period is provided, today's date (YYYY-MM-DD) is appended so
 * exports stay unique and sortable by name.
 */
export function formatExportFileName(
  input: string | DataGridExportFileName | undefined
): string {
  if (!input) return 'export';
  if (typeof input === 'string') return input.trim() || 'export';

  const parts: string[] = [slugify(input.base)];
  if (input.brand) parts.push(slugify(input.brand));

  if (input.period?.from && input.period?.to) {
    parts.push(`${input.period.from}_au_${input.period.to}`);
  } else if (input.period?.from) {
    parts.push(`depuis_${input.period.from}`);
  } else if (input.period?.to) {
    parts.push(`jusquau_${input.period.to}`);
  } else {
    parts.push(new Date().toISOString().slice(0, 10));
  }

  if (input.suffixes && input.suffixes.length > 0) {
    for (const s of input.suffixes) parts.push(slugify(s));
  }

  return parts.filter(Boolean).join('_');
}
